
import { t as systemText } from '../i18n';/**
 * Abuse-report reason options (PG-M6 — unified through the gateway
 * catalog registry).
 *
 * Source of truth: `GET /catalog/registry?catalogs=report_reasons` (PG-M5).
 * Registry items arrive as `{ code, label, meta, sort_order }`, already
 * tenant-resolved (platform rows + tenant overrides) and localized. The
 * gateway's AbuseReportController validates submitted reasons against the
 * same registry, so consuming it here means the picker can never drift
 * from what the API accepts.
 *
 * Fallback contract: when the registry is unreachable or returns no rows
 * (fresh database before the seeder ran), we fall back to the historical
 * general-purpose codes below — the same guarantee the gateway itself
 * makes (AbuseReport::REASONS fallback in reasonCodes()).
 *
 * Pure functions only — the HTTP call lives in FrontendService.
 */

/**
 * Historical general-purpose reason set (subset of AbuseReport::REASONS
 * appropriate for reporting a user/conversation from the chat surface).
 * Codes are the stable contract; labels are display fallbacks only.
 */
export const FALLBACK_REPORT_REASONS = Object.freeze([
  Object.freeze({ code: 'harassment', label: systemText("Harassment") }),
  Object.freeze({ code: 'scam', label: systemText("Scam or fraud") }),
  Object.freeze({ code: 'spam', label: systemText("Spam") }),
  Object.freeze({ code: 'underage', label: systemText("Underage") }),
  Object.freeze({ code: 'fake_photos', label: systemText("Fake photos") }),
  Object.freeze({ code: 'other', label: systemText("Other") }),
]);

/**
 * Normalize raw registry items into `{ code, label }` picker options.
 * Drops malformed rows; substitutes a humanized code when the label is
 * blank. Returns [] for non-arrays so callers can detect "no usable
 * registry data" and fall back.
 *
 * @param {Array<object>|null|undefined} items Raw registry items.
 * @returns {Array<{ code: string, label: string }>}
 */
export function normalizeReportReasons(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  const options = [];
  for (const item of items) {
    const code = typeof item?.code === 'string' ? item.code.trim() : '';
    if (!code || seen.has(code)) continue;
    seen.add(code);
    const rawLabel = typeof item?.label === 'string' ? item.label.trim() : '';
    const label = rawLabel
      || code.replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());
    options.push({ code, label });
  }
  return options;
}
